import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';
import adkWorkflowService from '../../services/adkWorkflow.service';

const ChangeRequestDialog = ({
  open,
  onClose,
  sectionId,
  sectionNumber,
  sectionTitle,
  projectId,
  studyId,
  onWorkflowInitiated
}) => {
  const [changeRequest, setChangeRequest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!changeRequest.trim()) {
      setError('Please describe the change you want to make.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await adkWorkflowService.initiateModificationWorkflow({
        sectionId,
        sectionNumber,
        changeRequest: changeRequest.trim(),
        projectId,
        studyId
      });

      // Callback with the result (includes approvalRequestId and plan)
      if (onWorkflowInitiated) {
        onWorkflowInitiated(result);
      }

      // Reset form and close
      setChangeRequest('');
      onClose();
    } catch (err) {
      console.error('Error initiating workflow:', err);
      setError(err.message || 'Failed to initiate modification workflow. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setChangeRequest('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        pb: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h6" component="div">
            Request Document Modification
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Section Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Chip 
              label={`Section ${sectionNumber || 'N/A'}`} 
              color="primary" 
              variant="outlined"
            />
            <Chip 
              label={sectionTitle || 'Untitled Section'} 
              variant="outlined"
            />
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe the change you want to make to this section. The AI agent will analyze your request,
          identify all affected sections using the Knowledge Graph, and create a detailed modification plan
          for your review.
        </Typography>

        <TextField
          autoFocus
          fullWidth
          multiline
          rows={8}
          label="Change Request"
          placeholder="Example: Change the storage temperature from '2-8°C' to '-20°C' throughout the protocol..."
          value={changeRequest}
          onChange={(e) => setChangeRequest(e.target.value)}
          disabled={isSubmitting}
          sx={{ mt: 2 }}
          helperText="Be as specific as possible. The AI will identify all related sections that need updates."
        />

        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>💡 Tip:</strong> The modification workflow will:
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Analyze your request using the Knowledge Graph</li>
              <li>Identify all affected sections (parent, child, and related sections)</li>
              <li>Generate a detailed modification plan for your approval</li>
              <li>Execute changes only after your approval</li>
            </ul>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)', pt: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
          disabled={isSubmitting || !changeRequest.trim()}
          sx={{ minWidth: 140 }}
        >
          {isSubmitting ? 'Analyzing...' : 'Start Analysis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeRequestDialog;


